import { ReportDataType, ReportResponseItem } from '../../../redux/context/reports/types'
import { FiscalYear } from '../../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { Language } from '../../../types/user/User'
import { BudgetingScenario } from '../../scenario/types/BudgetingScenario'
import { EnhancedStatementRow } from '../utils/dataEnhancement'

// Re-export VariableType from Table types
export { VariableType } from '../../../components/Table/types'
export type { RowBudgetingMethod } from '../../../components/Table/types'
export type { ActualRowType } from '../../../components/Table/hooks'

// Re-export related types
export type { Amendment } from '../../../types/amendment/Amendment'
export type { Investment } from '../../../types/investment/Investment'
export type { InvestmentFunding } from '../../../types/investment/InvestmentFunding'
export type { Company } from '../../../types/company/Company'
export type { Dimension } from '../../dimension/types/Dimension'

export type BalanceMap = Record<number, number | null>
export type BalanceGroupMap = Record<string | number, number | null>

export interface BalanceRegistry {
  elements: BalanceMap
  length: number
  start?: number
  end?: number
  groups: BalanceGroupMap
}

/**
 * Base interface for any data structure that contains BalanceRegistry fields
 * Used for generic data extraction across different data types (statements, drivers, etc.)
 */
export interface WithBalanceRegistries {
  budget?: BalanceRegistry
  budgets?: BalanceRegistry
  newBudgets?: BalanceRegistry
  actuals?: BalanceRegistry
  newActuals?: BalanceRegistry
  forecast?: BalanceRegistry
}

export enum StatementDataType {
  actuals = 'actuals',
  budget = 'budget',
  forecast = 'forecast'
}

export type BalanceRegistrySource = 'elements' | 'groups'

export interface StatementColumn {
  dataSource: BalanceRegistrySource // Identifies if this is a period or group column
  fiscalYear?: FiscalYear
  year?: number
  month?: number
  label: string
  dataType: StatementDataType
  columnType: ReportDataType
  periodGroup?: PeriodGroup | FiscalYear
  className?: string
  dataKey?: number // Key for accessing data from row[dataType].groups or row[dataType].elements
}

// Feature-specific types for React Query implementation
export type FinacialStatementType = 'incomeStatement' | 'assets' | 'liabilities'

export interface StatementRow extends WithBalanceRegistries {
  id: number
  code: number | string
  name: string

  accounts?: StatementRow[]

  // Properties
  isParent?: boolean
  statementType?: string // e.g. "pl" for profit & loss
  investable?: boolean
  depreciable?: boolean
  budgetable?: boolean
  amendable?: boolean
  counterAmendable?: boolean
  reallocated?: boolean

  // Driver-based budgeting
  budgetingMethod?: {
    method: 'driver' | 'absolute' | 'reference'
    source?: string
  }

  // Reallocation data
  unallocated?: {
    actuals?: BalanceRegistry
    forecast?: BalanceRegistry
    budget?: BalanceRegistry
    budgets?: BalanceRegistry
  }
  sources?: {
    actuals?: BalanceRegistry
    forecast?: BalanceRegistry
    budget?: BalanceRegistry
    budgets?: BalanceRegistry
  }
  targets?: {
    actuals?: BalanceRegistry
    forecast?: BalanceRegistry
    budget?: BalanceRegistry
    budgets?: BalanceRegistry
  }
}

export type DimensionStetementsResponse = ReportResponseItem<StatementRow>[]

export type FinancialStatementsResponse = ReportResponseItem<FinancialStatement>[]

/**
 * Investment report row - simplified structure for table display
 * Contains only the data needed for financial statement grid
 */
export interface InvestmentReportRow extends WithBalanceRegistries {
  id: number
  name: string
}

/**
 * Amendment report row - simplified structure for table display
 * Contains only the data needed for financial statement grid
 */
export interface AmendmentReportRow extends WithBalanceRegistries {
  id: number
  name: string
}

export type FinancialStatement = {
  incomeStatement: Statement
  assets: Statement
  liabilities: Statement
}

export type Statement = StatementRow[]

export type EnhancedFinancialStatement = {
  incomeStatement: EnhancedStatementRow[]
  assets: EnhancedStatementRow[]
  liabilities: EnhancedStatementRow[]
}

export interface FinancialStatementsFilters {
  companyId?: string
  dimensions?: string
  dimension?: string | null
  budgetingScenarioId?: BudgetingScenario['id']
  lng?: Language
  periodGroups?: PeriodGroup[]
}

export interface FinancialStatementsFilterKeys {
  companyId: string
  budgetingScenarioId?: string
  periodGroups?: Array<Pick<PeriodGroup, 'id' | 'startDate' | 'endDate'>>
  dimensions?: string[]
  dimension?: string | null
  lng: string
}

export interface FinancialStatementsFilterParams {
  companyId: string
  budgetingScenarioId?: string
  periodGroups?: any
  dimensions?: string
  dimension?: string | null
  lng: string
}

export interface DimensionQueryParams {
  companyId: string
  path: string[]
  parentId: string | null
  accountCode?: string | number | null
  statementRowId?: string | number | null
  dimensionIds?: (string | null)[]
  budgetingScenarioId?: BudgetingScenario['id']
  periodGroups?: PeriodGroup[]
}

export type FinancialStatementRowAction =
  | 'create-investment'
  | 'create-divestment'
  | 'show-deprecations'
  | 'edit'
  | 'delete'
  | 'open-budget-modal'
